import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { FileText, Download, Trash2, Edit3, Plus, X, RefreshCw, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BAR_NAMES_LOCAL = {
  'lobby': 'Lobby Bar',
  'servis': 'Servis Bar',
  'vitamin': 'Vitamin Bar',
  'havuz': 'Havuz Bar',
  'plaj': 'Plaj Bar',
  'amfi': 'Amfi Bar',
  'teras': 'Teras Bar',
  'alacarte': 'Alacarte Bar',
  'minibar': 'Minibar',
};

const EMPTY_MV = { guest_count: '', avg_eur_rate: '', bar_total_tl: '', bar_total_eur: '', minibar_total_tl: '', minibar_total_eur: '', per_person_tl: '', per_person_eur: '', total_tl: '', total_eur: '', per_person_grand_tl: '', per_person_grand_eur: '' };
const emptyForm = () => {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = (() => { const d = new Date(); d.setDate(d.getDate() - 29); return d.toISOString().slice(0, 10); })();
  return {
    id: null,
    name: '',
    date_from: monthAgo,
    date_to: today,
    bars: '',
    include_consumables: false,
    manual_values: { ...EMPTY_MV },
    note: '',
  };
};

const fmtNum = (v, dec = 2) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('tr-TR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
};

// Türkçe/İngilizce sayı formatını akıllıca parse eder:
// "3.361.030,18" → 3361030.18   "3361030.18" → 3361030.18   "3361030,18" → 3361030.18
const parseTrNum = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const s = String(v).trim();
  if (!s) return null;
  // Hem virgül hem nokta var → Türkçe (nokta=binlik, virgül=ondalık)
  if (s.includes(',') && s.includes('.')) {
    const normalized = s.replace(/\./g, '').replace(',', '.');
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : null;
  }
  // Sadece virgül var → Türkçe ondalık
  if (s.includes(',') && !s.includes('.')) {
    const n = parseFloat(s.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  // Sadece nokta var: birden fazla nokta = Türkçe binlik (3.361.030), tek nokta = İngilizce ondalık
  if (s.includes('.') && !s.includes(',')) {
    const parts = s.split('.');
    if (parts.length > 2) {
      // 3.361.030 gibi → binlik
      const n = parseFloat(parts.join(''));
      return Number.isFinite(n) ? n : null;
    }
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
};

export function ManualReportModal({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [autoValues, setAutoValues] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [selectedBars, setSelectedBars] = useState(new Set(Object.keys(BAR_NAMES_LOCAL)));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const t = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/manual-reports`, { headers: { Authorization: `Bearer ${t}` } });
      const j = await r.json();
      setItems(j.items || []);
    } catch (e) {
      toast.error('Manuel raporlar yüklenemedi');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const resetForm = () => {
    setForm(emptyForm());
    setAutoValues(null);
    setSelectedBars(new Set(Object.keys(BAR_NAMES_LOCAL)));
  };

  const toggleBar = (id) => {
    setSelectedBars(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const barsCsv = () => {
    if (selectedBars.size === Object.keys(BAR_NAMES_LOCAL).length || selectedBars.size === 0) return '';
    return Array.from(selectedBars).join(',');
  };

  const fetchAuto = async () => {
    if (!form.date_from || !form.date_to) { toast.error('Tarih aralığı seçin'); return; }
    setAutoLoading(true);
    try {
      const t = localStorage.getItem('token');
      const p = new URLSearchParams({ date_from: form.date_from, date_to: form.date_to, bars: barsCsv() });
      const r = await fetch(`${API_URL}/api/manual-reports/preview-defaults?${p}`, { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) throw new Error('Otomatik değerler alınamadı');
      const j = await r.json();
      setAutoValues(j);
      toast.success('Otomatik değerler getirildi');
    } catch (e) {
      toast.error(e.message);
    } finally { setAutoLoading(false); }
  };

  const applyAutoToManual = () => {
    if (!autoValues) return;
    setForm(f => ({ ...f, manual_values: {
      guest_count: autoValues.guest_count ?? '',
      avg_eur_rate: autoValues.avg_eur_rate ?? '',
      per_person_tl: autoValues.per_person_tl ?? '',
      per_person_eur: autoValues.per_person_eur ?? '',
      total_tl: autoValues.total_tl ?? '',
      total_eur: autoValues.total_eur ?? '',
      per_person_grand_tl: autoValues.per_person_grand_tl ?? '',
      per_person_grand_eur: autoValues.per_person_grand_eur ?? '',
    }}));
    toast.success('Otomatik değerler forma dolduruldu');
  };

  const save = async (andDownload = false) => {
    if (!form.name.trim()) { toast.error('Rapor adı gerekli'); return; }
    if (!form.date_from || !form.date_to) { toast.error('Tarih aralığı gerekli'); return; }
    setSaving(true);
    try {
      const t = localStorage.getItem('token');
      const body = {
        name: form.name.trim(),
        date_from: form.date_from,
        date_to: form.date_to,
        bars: barsCsv(),
        include_consumables: !!form.include_consumables,
        manual_values: Object.fromEntries(Object.entries(form.manual_values).map(([k, v]) => [k, parseTrNum(v)])),
        note: form.note || '',
      };
      const url = form.id ? `${API_URL}/api/manual-reports/${form.id}` : `${API_URL}/api/manual-reports`;
      const method = form.id ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` }, body: JSON.stringify(body) });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.detail || 'Kayıt başarısız'); }
      const saved = await r.json();
      toast.success(form.id ? 'Rapor güncellendi' : 'Rapor kaydedildi');
      // Kaydedilen raporun id'si forma bind edilsin ki PDF İndir butonu görünsün.
      setForm(f => ({ ...f, id: saved.id || form.id }));
      load();
      if (andDownload) {
        await downloadPdf(saved);
      }
    } catch (e) {
      toast.error(e.message);
    } finally { setSaving(false); }
  };

  const editItem = (it) => {
    setForm({
      id: it.id,
      name: it.name || '',
      date_from: it.date_from,
      date_to: it.date_to,
      bars: it.bars || '',
      include_consumables: !!it.include_consumables,
      manual_values: {
        guest_count: it.manual_values?.guest_count ?? '',
        avg_eur_rate: it.manual_values?.avg_eur_rate ?? '',
        bar_total_tl: it.manual_values?.bar_total_tl ?? '',
        bar_total_eur: it.manual_values?.bar_total_eur ?? '',
        minibar_total_tl: it.manual_values?.minibar_total_tl ?? '',
        minibar_total_eur: it.manual_values?.minibar_total_eur ?? '',
        per_person_tl: it.manual_values?.per_person_tl ?? '',
        per_person_eur: it.manual_values?.per_person_eur ?? '',
        total_tl: it.manual_values?.total_tl ?? '',
        total_eur: it.manual_values?.total_eur ?? '',
        per_person_grand_tl: it.manual_values?.per_person_grand_tl ?? '',
        per_person_grand_eur: it.manual_values?.per_person_grand_eur ?? '',
      },
      note: it.note || '',
    });
    if (it.bars) setSelectedBars(new Set(it.bars.split(',').filter(Boolean)));
    else setSelectedBars(new Set(Object.keys(BAR_NAMES_LOCAL)));
    setAutoValues(null);
    toast.info('Düzenleme modu aktif');
  };

  const deleteItem = async (it) => {
    if (!window.confirm(`"${it.name}" silinsin mi?`)) return;
    const t = localStorage.getItem('token');
    const r = await fetch(`${API_URL}/api/manual-reports/${it.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${t}` } });
    if (r.ok) { toast.success('Silindi'); load(); if (form.id === it.id) resetForm(); }
    else toast.error('Silinemedi');
  };

  const downloadPdf = async (it) => {
    if (!it?.id) { toast.error('Önce raporu kaydedin'); return; }
    const t = localStorage.getItem('token');
    try {
      toast.info('PDF hazırlanıyor…');
      const r = await fetch(`${API_URL}/api/manual-reports/${it.id}/pdf?token=${t}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!r.ok) {
        const err = await r.text().catch(() => '');
        throw new Error(`PDF indirilemedi (${r.status})${err ? ': ' + err.slice(0, 120) : ''}`);
      }
      const blob = await r.blob();
      if (blob.size < 500) throw new Error('Boş PDF döndü');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(it.name || 'manuel-rapor').replace(/[^\w\-. ]+/g, '_')}_${it.date_from || ''}_${it.date_to || ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success('PDF indirildi');
    } catch (e) {
      toast.error(e.message || 'PDF indirilemedi');
    }
  };

  // v7: TL ↔ EUR otomatik dönüşüm.
  // Kullanıcı avg_eur_rate girdikten sonra, herhangi bir TL alanına yazdığında
  // eşdeğer EUR otomatik hesaplanır (ve tersi). Boş alanda çalışır.
  const TL_TO_EUR = {
    bar_total_tl: 'bar_total_eur',
    minibar_total_tl: 'minibar_total_eur',
    per_person_tl: 'per_person_eur',
    total_tl: 'total_eur',
    per_person_grand_tl: 'per_person_grand_eur',
  };
  const EUR_TO_TL = Object.fromEntries(Object.entries(TL_TO_EUR).map(([tl, eur]) => [eur, tl]));

  const setMV = (k, v) => setForm(f => {
    const next = { ...f.manual_values, [k]: v };
    const rate = parseTrNum(next.avg_eur_rate);
    if (rate && rate > 0) {
      const num = parseTrNum(v);
      // TL → EUR
      if (k in TL_TO_EUR && num !== null && Number.isFinite(num)) {
        const eurKey = TL_TO_EUR[k];
        // Türkçe ondalık gösterim (virgül, 2 hane)
        next[eurKey] = (num / rate).toFixed(2).replace('.', ',');
      }
      // EUR → TL
      else if (k in EUR_TO_TL && num !== null && Number.isFinite(num)) {
        const tlKey = EUR_TO_TL[k];
        next[tlKey] = (num * rate).toFixed(2).replace('.', ',');
      }
      // avg_eur_rate değişirse: mevcut TL değerlerinden EUR'ları yeniden hesapla
      else if (k === 'avg_eur_rate') {
        for (const [tlKey, eurKey] of Object.entries(TL_TO_EUR)) {
          const tlNum = parseTrNum(next[tlKey]);
          if (tlNum !== null && Number.isFinite(tlNum) && tlNum !== 0) {
            next[eurKey] = (tlNum / rate).toFixed(2).replace('.', ',');
          }
        }
      }
    }
    return { ...f, manual_values: next };
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-y-auto py-6" onClick={onClose} data-testid="manual-report-modal">
      <div className="bg-white rounded-xl shadow-2xl w-[95%] max-w-6xl my-4" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-indigo-50 to-white rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Manuel Rapor</h2>
              <p className="text-xs text-slate-500">Konsolide raporun özet satırlarını manuel değerlerle üret. Ürün tablosu sistem verisinden gelir.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md" data-testid="mr-close-btn">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 p-6">
          {/* Left: Saved reports list */}
          <div className="lg:col-span-2">
            <Card className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span>Kayıtlı Manuel Raporlar</span>
                  <Button size="sm" variant="ghost" onClick={resetForm} data-testid="mr-new-btn">
                    <Plus className="w-4 h-4 mr-1" /> Yeni
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {loading ? (
                  <div className="text-center py-6 text-slate-400 text-sm">Yükleniyor…</div>
                ) : items.length === 0 ? (
                  <div className="text-center py-8 text-slate-400 text-sm border border-dashed border-slate-200 rounded-lg">
                    Henüz manuel rapor yok.
                    <br />Sağdaki formu doldurarak ilkini oluşturun.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                    {items.map(it => (
                      <div key={it.id} className={`border rounded-lg p-3 hover:shadow-sm transition-all ${form.id === it.id ? 'border-indigo-400 bg-indigo-50/40' : 'border-slate-200'}`} data-testid={`mr-item-${it.id}`}>
                        <div className="font-semibold text-sm text-slate-800 truncate">{it.name}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{it.date_from} → {it.date_to}</div>
                        <div className="text-[11px] text-slate-500">
                          Kişi: <b>{fmtNum(it.manual_values?.guest_count, 0)}</b> · Toplam TL: <b>{fmtNum(it.manual_values?.total_tl)}</b>
                        </div>
                        <div className="flex gap-1 mt-2">
                          <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => editItem(it)} data-testid={`mr-edit-${it.id}`}>
                            <Edit3 className="w-3 h-3 mr-1" /> Düzenle
                          </Button>
                          <Button size="sm" className="h-7 text-xs flex-1 bg-blue-600 hover:bg-blue-700" onClick={() => downloadPdf(it)} data-testid={`mr-pdf-${it.id}`}>
                            <Download className="w-3 h-3 mr-1" /> PDF
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 hover:bg-red-50" onClick={() => deleteItem(it)} data-testid={`mr-del-${it.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  {form.id ? <><Edit3 className="w-4 h-4 text-indigo-600" /> Raporu Düzenle</> : <><Plus className="w-4 h-4 text-indigo-600" /> Yeni Manuel Rapor</>}
                </CardTitle>
                <CardDescription className="text-xs">Değerler manuel giriliyor; ürün tablosu sistem verisinden gelir.</CardDescription>
              </CardHeader>
              <CardContent className="pt-0 space-y-4">
                {/* Name */}
                <div>
                  <Label className="text-xs text-slate-600">Rapor Adı *</Label>
                  <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Örn: Temmuz 2026 Manuel" data-testid="mr-name" className="h-9" />
                </div>

                {/* Date range */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600">Başlangıç Tarihi *</Label>
                    <Input type="date" value={form.date_from} onChange={(e) => setForm(f => ({ ...f, date_from: e.target.value }))} data-testid="mr-date-from" className="h-9" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Bitiş Tarihi *</Label>
                    <Input type="date" value={form.date_to} onChange={(e) => setForm(f => ({ ...f, date_to: e.target.value }))} data-testid="mr-date-to" className="h-9" />
                  </div>
                </div>

                {/* Bars */}
                <div>
                  <Label className="text-xs text-slate-600">Barlar</Label>
                  <div className="grid grid-cols-3 gap-1.5 mt-1">
                    {Object.entries(BAR_NAMES_LOCAL).map(([k, n]) => (
                      <label key={k} className="flex items-center gap-1.5 text-xs text-slate-700 border border-slate-200 rounded px-2 py-1 hover:bg-slate-50 cursor-pointer">
                        <input type="checkbox" checked={selectedBars.has(k)} onChange={() => toggleBar(k)} className="w-3.5 h-3.5 accent-indigo-600" data-testid={`mr-bar-${k}`} />
                        {n}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Auto values fetch */}
                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-xs font-semibold text-slate-700">Otomatik Değerler (Sistem)</div>
                      <div className="text-[11px] text-slate-500">Karşılaştırma amaçlı — PDF kapağında da gösterilir.</div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={fetchAuto} disabled={autoLoading} className="h-8 text-xs" data-testid="mr-fetch-auto">
                        <RefreshCw className={`w-3 h-3 mr-1 ${autoLoading ? 'animate-spin' : ''}`} /> {autoLoading ? 'Hesaplanıyor…' : 'Getir'}
                      </Button>
                      <Button size="sm" onClick={applyAutoToManual} disabled={!autoValues} className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700" data-testid="mr-apply-auto">
                        <Sparkles className="w-3 h-3 mr-1" /> Forma Doldur
                      </Button>
                    </div>
                  </div>
                  {autoValues && (
                    <div className="grid grid-cols-3 gap-2 text-[11px] text-slate-600">
                      <div>Kişi: <b className="text-slate-800">{fmtNum(autoValues.guest_count, 0)}</b></div>
                      <div>Ort. Kur: <b className="text-slate-800">{fmtNum(autoValues.avg_eur_rate, 4)}</b></div>
                      <div>KB TL: <b className="text-slate-800">{fmtNum(autoValues.per_person_tl)}</b></div>
                      <div>KB EUR: <b className="text-slate-800">{fmtNum(autoValues.per_person_eur)}</b></div>
                      <div>Top TL: <b className="text-slate-800">{fmtNum(autoValues.total_tl)}</b></div>
                      <div>Top EUR: <b className="text-slate-800">{fmtNum(autoValues.total_eur)}</b></div>
                    </div>
                  )}
                </div>

                {/* Manual values */}
                <div>
                  <div className="text-xs font-semibold text-slate-700 mb-1">Manuel Değerler</div>
                  <div className="text-[10px] text-slate-500 mb-2">💡 Sayıları Türkçe (<b>3.361.030,18</b>) veya sade (<b>3361030.18</b>) yazabilirsin. <b>Kişi Başı ve Genel Toplamı boş bırakırsanız otomatik hesaplanır.</b></div>

                  <div className="text-[11px] font-semibold text-emerald-700 mt-3 mb-1">📌 Temel Değerler (bunları girin — geri kalanı otomatik hesaplanır)</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px] text-slate-500">Kişi Sayısı</Label>
                      <Input type="text" inputMode="numeric" value={form.manual_values.guest_count} onChange={(e) => setMV('guest_count', e.target.value)} data-testid="mr-mv-guest-count" className="h-9" placeholder="12583" />
                    </div>
                    <div>
                      <Label className="text-[11px] text-slate-500">Ortalama EUR Kuru</Label>
                      <Input type="text" inputMode="decimal" value={form.manual_values.avg_eur_rate} onChange={(e) => setMV('avg_eur_rate', e.target.value)} data-testid="mr-mv-avg-eur-rate" className="h-9" placeholder="53,1862" />
                    </div>
                    <div>
                      <Label className="text-[11px] text-slate-500">Ana Bar Toplam TL</Label>
                      <Input type="text" inputMode="decimal" value={form.manual_values.bar_total_tl} onChange={(e) => setMV('bar_total_tl', e.target.value)} data-testid="mr-mv-bar-total-tl" className="h-9" placeholder="3.361.030,18" />
                    </div>
                    <div>
                      <Label className="text-[11px] text-slate-500">Ana Bar Toplam EUR</Label>
                      <Input type="text" inputMode="decimal" value={form.manual_values.bar_total_eur} onChange={(e) => setMV('bar_total_eur', e.target.value)} data-testid="mr-mv-bar-total-eur" className="h-9" placeholder="63.287,47" />
                    </div>
                    <div>
                      <Label className="text-[11px] text-slate-500">Minibar Toplam TL</Label>
                      <Input type="text" inputMode="decimal" value={form.manual_values.minibar_total_tl} onChange={(e) => setMV('minibar_total_tl', e.target.value)} data-testid="mr-mv-minibar-total-tl" className="h-9" placeholder="473.697,40" />
                    </div>
                    <div>
                      <Label className="text-[11px] text-slate-500">Minibar Toplam EUR</Label>
                      <Input type="text" inputMode="decimal" value={form.manual_values.minibar_total_eur} onChange={(e) => setMV('minibar_total_eur', e.target.value)} data-testid="mr-mv-minibar-total-eur" className="h-9" placeholder="8.923,12" />
                    </div>
                  </div>

                  <details className="mt-3 border border-slate-200 rounded p-2 bg-slate-50/50">
                    <summary className="text-[11px] font-semibold text-slate-600 cursor-pointer">⚙️ Gelişmiş — Alt Toplamları Elle Belirle (opsiyonel)</summary>
                    <div className="text-[10px] text-slate-500 mt-1 mb-2">Boş bırakılırsa yukarıdaki temel değerlerden otomatik hesaplanır.</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[11px] text-slate-500">Kişi Başı Maliyet TL</Label>
                        <Input type="text" inputMode="decimal" value={form.manual_values.per_person_tl} onChange={(e) => setMV('per_person_tl', e.target.value)} data-testid="mr-mv-per-person-tl" className="h-9" placeholder="otomatik" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-500">Kişi Başı Maliyet EUR</Label>
                        <Input type="text" inputMode="decimal" value={form.manual_values.per_person_eur} onChange={(e) => setMV('per_person_eur', e.target.value)} data-testid="mr-mv-per-person-eur" className="h-9" placeholder="otomatik" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-500">Bonus ve Minibar Toplam TL</Label>
                        <Input type="text" inputMode="decimal" value={form.manual_values.total_tl} onChange={(e) => setMV('total_tl', e.target.value)} data-testid="mr-mv-total-tl" className="h-9" placeholder="otomatik (bar+minibar)" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-500">Bonus ve Minibar Toplam EUR</Label>
                        <Input type="text" inputMode="decimal" value={form.manual_values.total_eur} onChange={(e) => setMV('total_eur', e.target.value)} data-testid="mr-mv-total-eur" className="h-9" placeholder="otomatik (bar+minibar)" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-500">Kişi Başı Genel Toplam TL</Label>
                        <Input type="text" inputMode="decimal" value={form.manual_values.per_person_grand_tl} onChange={(e) => setMV('per_person_grand_tl', e.target.value)} data-testid="mr-mv-per-person-grand-tl" className="h-9" placeholder="otomatik" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-500">Kişi Başı Genel Toplam EUR</Label>
                        <Input type="text" inputMode="decimal" value={form.manual_values.per_person_grand_eur} onChange={(e) => setMV('per_person_grand_eur', e.target.value)} data-testid="mr-mv-per-person-grand-eur" className="h-9" placeholder="otomatik" />
                      </div>
                    </div>
                  </details>
                </div>

                {/* Note */}
                <div>
                  <Label className="text-xs text-slate-600">Not (opsiyonel)</Label>
                  <Input value={form.note} onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Rapor hakkında kısa not…" data-testid="mr-note" className="h-9" />
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 justify-end pt-2 border-t">
                  {form.id && (
                    <Button variant="outline" onClick={resetForm} data-testid="mr-cancel-edit">İptal</Button>
                  )}
                  <Button onClick={() => save(false)} disabled={saving} variant="outline" data-testid="mr-save-btn">
                    {saving ? 'Kaydediliyor…' : (form.id ? 'Güncelle' : 'Kaydet')}
                  </Button>
                  <Button onClick={() => save(true)} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="mr-save-and-download-btn">
                    <Download className="w-4 h-4 mr-1" /> {saving ? 'İşleniyor…' : 'Kaydet ve PDF İndir'}
                  </Button>
                  {form.id && (
                    <Button onClick={() => downloadPdf(form)} className="bg-blue-600 hover:bg-blue-700" data-testid="mr-form-pdf-btn">
                      <Download className="w-4 h-4 mr-1" /> Sadece PDF İndir
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
